<template>
    <main class="main pt-5" id="top">
        <SideBarNav />
        <TopBarNav />

        <div class="content">
            <div class="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 class="mb-1">Media library</h2>
                    <p class="text-muted mb-0">
                        Total: {{ counts.total }} ·
                        En uso: {{ counts.used }} ·
                        Huérfanos: {{ counts.orphans }}
                    </p>
                </div>

                <div class="btn-group" role="group">
                    <button type="button" class="btn btn-outline-secondary"
                            :class="{ active: filter === 'all' }" @click="setFilter('all')">Todas</button>
                    <button type="button" class="btn btn-outline-secondary"
                            :class="{ active: filter === 'used' }" @click="setFilter('used')">En uso</button>
                    <button type="button" class="btn btn-outline-secondary"
                            :class="{ active: filter === 'orphans' }" @click="setFilter('orphans')">Huérfanos</button>
                </div>
            </div>

            <div v-if="loading" class="text-center py-5 text-muted">Cargando...</div>

            <div v-else>
                <h3 class="mt-2 mb-3">Tabla de imágenes</h3>
                <div class="table-responsive">
                    <table class="table table-sm table-striped align-middle">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Preview</th>
                                <th>Path</th>
                                <th>Dimensiones</th>
                                <th>Tamaño</th>
                                <th>Estado</th>
                                <th class="text-end">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="img in items" :key="'row-' + img.id">
                                <td>{{ img.id }}</td>
                                <td>
                                    <img :src="thumbUrl(img.path)" :alt="'image ' + img.id"
                                         style="height: 40px; width: 40px; object-fit: cover; background: #f4f4f4;" />
                                </td>
                                <td class="small text-break" :title="img.path">{{ img.path }}</td>
                                <td class="text-nowrap">
                                    <span v-if="img.width && img.height">{{ img.width }} × {{ img.height }}</span>
                                    <span v-else class="text-muted">—</span>
                                </td>
                                <td class="text-nowrap">{{ formatSize(img.size_bytes) }}</td>
                                <td>
                                    <span v-if="img.in_use" class="badge bg-success">En uso</span>
                                    <span v-else class="badge bg-warning text-dark">Huérfano</span>
                                </td>
                                <td class="text-end">
                                    <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(img)" :disabled="deletingId === img.id">
                                        <span v-if="deletingId === img.id">...</span>
                                        <span v-else>Eliminar</span>
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="items.length === 0">
                                <td colspan="7" class="text-center text-muted py-3">No hay imágenes para mostrar.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <nav class="mt-4 d-flex justify-content-center">
                    <button class="btn btn-outline-secondary me-2" :disabled="page <= 1 || loading" @click="prevPage">Anterior</button>
                    <span class="align-self-center">Página {{ page }}</span>
                    <button class="btn btn-outline-secondary ms-2" :disabled="items.length < limit || loading" @click="nextPage">Siguiente</button>
                </nav>
            </div>
        </div>
    </main>
</template>

<script>
import axios from 'axios'
import SideBarNav from '@/components/SideBarNav.vue'
import TopBarNav from '@/components/TopBarNav.vue'

export default {
    name: 'MediaView',
    components: { SideBarNav, TopBarNav },
    data() {
        return {
            items: [],
            counts: { total: 0, used: 0, orphans: 0 },
            filter: 'all',
            page: 1,
            limit: 60,
            loading: false,
            deletingId: null,
        }
    },
    created() {
        this.load()
    },
    methods: {
        thumbUrl(path) {
            return this.$url + '/thumb/200/' + path
        },
        formatSize(bytes) {
            if (bytes === null || bytes === undefined) return '—'
            const n = Number(bytes)
            if (!isFinite(n) || n <= 0) return '—'
            if (n < 1024) return n + ' B'
            if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB'
            return (n / (1024 * 1024)).toFixed(2) + ' MB'
        },
        async load() {
            this.loading = true
            try {
                const res = await axios.get(this.$url + '/media', {
                    params: { page: this.page, limit: this.limit, filter: this.filter },
                    headers: { Authorization: this.$store.state.token },
                })
                this.items = res.data.items || []
                this.counts = res.data.counts || this.counts
            } catch (err) {
                this.$notify({ type: 'error', title: 'Error', text: 'No se pudo cargar la biblioteca' })
                console.error(err)
            } finally {
                this.loading = false
            }
        },
        setFilter(f) {
            if (this.filter === f) return
            this.filter = f
            this.page = 1
            this.load()
        },
        prevPage() {
            if (this.page > 1) {
                this.page--
                this.load()
            }
        },
        nextPage() {
            this.page++
            this.load()
        },
        async confirmDelete(img) {
            const warning = img.in_use
                ? `La imagen #${img.id} está EN USO. ¿Eliminar de todos modos?`
                : `¿Eliminar la imagen #${img.id}?`
            if (!window.confirm(warning)) return

            this.deletingId = img.id
            try {
                await axios.post(this.$url + '/media/delete/' + img.id, {}, {
                    headers: { Authorization: this.$store.state.token },
                })
                this.items = this.items.filter(i => i.id !== img.id)
                this.counts.total = Math.max(0, this.counts.total - 1)
                if (img.in_use) {
                    this.counts.used = Math.max(0, this.counts.used - 1)
                } else {
                    this.counts.orphans = Math.max(0, this.counts.orphans - 1)
                }
                this.$notify({ type: 'success', title: 'Eliminada', text: `Imagen #${img.id} eliminada` })
            } catch (err) {
                this.$notify({ type: 'error', title: 'Error', text: 'No se pudo eliminar' })
                console.error(err)
            } finally {
                this.deletingId = null
            }
        },
    },
}
</script>
